import React, { useState, useRef, forwardRef, useCallback, useEffect } from 'react';
import { fetchRsvpOpen } from '../../../utils/rsvpStatus';
import HTMLFlipBook from 'react-pageflip';
import '../../../styles/LeonCamping.css';
import bookOpeningSound from '../../../assets/sounds/book-opening.mp3';
import bgMusic from '../../../assets/sounds/na-leon-camp-background-music.mp3';
import logicaLogo from '../../../assets/images/Logica (Black and White).webp';
import leonPhoto from '../../../assets/images/na-leon-v-fortuna.webp';
import galPhoto1 from '../../../assets/images/na-leon-pg1.webp';
import galPhoto2 from '../../../assets/images/na-leon-pg2.webp';
import galPhoto3 from '../../../assets/images/na-leon-pg3.webp';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/* ─── Page wrapper ───────────────────────────────────────────── */
const Page = forwardRef(({ children, className = '' }, ref) => (
  <div className={`lc-page ${className}`} ref={ref}>
    {children}
  </div>
));
Page.displayName = 'Page';

/* ─── Main Component ─────────────────────────────────────────── */
export default function LeonCamping() {
  const [phase, setPhase]             = useState('cover');
  const [currentPage, setCurrentPage] = useState(0);
  const [isFlipping, setIsFlipping]   = useState(false);
  const [sparklePos, setSparklePos]   = useState({ top: 0, left: 0 });
  const [flipFx, setFlipFx]           = useState(null);
  const [bookReady, setBookReady]     = useState(false);
  const [isMobile, setIsMobile]       = useState(window.innerWidth <= 768);
  const [showTapHint, setShowTapHint] = useState(false);
  const [countdown, setCountdown]     = useState({ d: 0, h: 0, m: 0, s: 0 });
  const [showRsvp, setShowRsvp]       = useState(false);
  const [rsvpOpen, setRsvpOpen]       = useState(true);
  const [rsvpDone, setRsvpDone]       = useState(false);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [rsvpError, setRsvpError]     = useState('');
  const [rsvpData, setRsvpData]       = useState({ name: '', attending: '', guests: '1', message: '' });
  const [galleryIdx, setGalleryIdx]   = useState(null);
  const [bgPlaying, setBgPlaying]     = useState(false);

  const galleryPhotos = [galPhoto1, galPhoto2, galPhoto3];

  const bookRef    = useRef(null);
  const audioRef   = useRef(null);
  const bgRef      = useRef(null);
  const wrapperRef = useRef(null);

  /* ── Event data ──────────────────────────────────────────── */
  const ev = {
    childName:   'N.A',
    childFull:   'N.A Leon Villamor Fortuna',
    father:      'Noel Adrian Fortuna',
    mother:      'Sheila Marie Villamor',
    eventDate:   'May 9, 2026',
    eventDay:    'Saturday',
    timeChurch:  '2:00 PM',
    timeRec:     '3:00 PM',
    timeEnd:     '5:00 PM',
    church:      'Our Lady of the Annunciation Parish',
    churchAbbr:  'OLAP',
    churchAddr:  'Mindanao Ave., Quezon City',
    venue:       'Peri-Peri',
    venueAddr:   'Mindanao Ave., Quezon City',
    verse:       '"Before I formed you in the womb I knew you, before you were born I set you apart."',
    verseRef:    'Jeremiah 1:5',
    hashtag:     '#NATurnsONE',
    rsvpBy:      'May 1, 2026',
    program: [
      { time: '2:00 PM', event: 'Arrival of Guests'            },
      { time: '2:30 PM', event: 'Christening Mass'              },
      { time: '3:00 PM', event: 'Photo Session'                 },
      { time: '3:30 PM', event: 'Reception at Peri-Peri Begins' },
      { time: '4:00 PM', event: 'Lunch & Festivities'           },
      { time: '4:30 PM', event: 'Blowing of Candles 🎂'        },
      { time: '5:00 PM', event: 'End of Celebration'            },
    ],
  };

  const TOTAL = 8;

  /* ── RSVP open/closed check ──────────────────────────────── */
  useEffect(() => {
    fetchRsvpOpen('camping-na').then(setRsvpOpen);
  }, []);

  /* ── Countdown ───────────────────────────────────────────── */
  useEffect(() => {
    const target = new Date('May 9, 2026 00:00:00').getTime();
    const tick = () => {
      const diff = target - Date.now();
      if (diff > 0) setCountdown({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  /* ── Resize ──────────────────────────────────────────────── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  /* ── Page-flip audio ────────────────────────────────────── */
  useEffect(() => {
    const a = new Audio(bookOpeningSound);
    a.volume = 0.4; a.preload = 'auto'; a.load();
    a.play().then(() => { a.pause(); a.currentTime = 0; }).catch(() => {});
    audioRef.current = a;
    return () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } };
  }, []);

  /* ── Background music ────────────────────────────────────── */
  useEffect(() => {
    const bg = new Audio(bgMusic);
    bg.loop = true;
    bg.volume = 0.35;
    bg.preload = 'auto';
    bgRef.current = bg;
    return () => { bg.pause(); bg.src = ''; };
  }, []);

  const toggleBg = () => {
    const bg = bgRef.current;
    if (!bg) return;
    if (bgPlaying) { bg.pause(); setBgPlaying(false); }
    else { bg.play().catch(() => {}); setBgPlaying(true); }
  };

  /* ── Tap hint ────────────────────────────────────────────── */
  useEffect(() => {
    if (phase !== 'cover') return;
    const t = setTimeout(() => setShowTapHint(true), 2500);
    return () => clearTimeout(t);
  }, [phase]);

  /* ── Book ready ──────────────────────────────────────────── */
  useEffect(() => {
    if (phase === 'open') {
      const t = setTimeout(() => { window.dispatchEvent(new Event('resize')); setBookReady(true); }, 120);
      return () => clearTimeout(t);
    }
  }, [phase]);

  /* ── Handlers ────────────────────────────────────────────── */
  const playPage = useCallback(() => {
    if (audioRef.current) { audioRef.current.currentTime = 0; audioRef.current.play().catch(() => {}); }
  }, []);

  const openBook  = () => {
    if (phase === 'cover') {
      setShowTapHint(false);
      setPhase('open');
      if (bgRef.current && !bgPlaying) {
        bgRef.current.play().catch(() => {});
        setBgPlaying(true);
      }
    }
  };
  const closeBook = () => {
    if (bookRef.current) bookRef.current.pageFlip().flip(0);
    setTimeout(() => { setCurrentPage(0); setPhase('cover'); }, 350);
  };

  const calcSparkle = useCallback(() => {
    if (!wrapperRef.current) return;
    const el = wrapperRef.current.querySelector('.stf__parent');
    if (!el) return;
    const wr = wrapperRef.current.getBoundingClientRect();
    const br = el.getBoundingClientRect();
    setSparklePos({ top: br.top - wr.top - 20, left: br.right - wr.left - 55 });
  }, []);

  /* ── Random flip effects ─────────────────────────────────── */
  const FX_POOL = [
    { emojis: ['🍃','🌿','🍂','🌱','☘️'], cls: 'lc-fx-leaves' },
    { emojis: ['✨','⭐','💫','✦','⋆'],   cls: 'lc-fx-stars'  },
    { emojis: ['🐻','🦊','🐰','🦉','🐿️'], cls: 'lc-fx-animals'},
    { emojis: ['🏕️','🔥','🌲','⛺','🎒'], cls: 'lc-fx-camp'   },
    { emojis: ['🎂','🎈','🎉','🧁','🎊'], cls: 'lc-fx-party'  },
  ];

  const triggerFlipFx = useCallback(() => {
    const fx = FX_POOL[Math.floor(Math.random() * FX_POOL.length)];
    // Random origin: corners + sides
    const origins = [
      { ox: '0%',   oy: '0%'   }, // top-left
      { ox: '100%', oy: '0%'   }, // top-right
      { ox: '0%',   oy: '100%' }, // bottom-left
      { ox: '100%', oy: '100%' }, // bottom-right
      { ox: '50%',  oy: '0%'   }, // top-center
      { ox: '50%',  oy: '100%' }, // bottom-center
      { ox: '0%',   oy: '50%'  }, // mid-left
      { ox: '100%', oy: '50%'  }, // mid-right
    ];
    const origin = origins[Math.floor(Math.random() * origins.length)];
    const particles = Array.from({ length: 10 }, (_, i) => ({
      emoji: fx.emojis[i % fx.emojis.length],
      id: i,
      x: (Math.random() - 0.5) * 260,
      y: (Math.random() - 0.5) * 260,
      r: (Math.random() - 0.5) * 400,
      s: 0.7 + Math.random() * 0.6,
      d: (Math.random() * 0.25).toFixed(2),
    }));
    setFlipFx({ cls: fx.cls, particles, origin, key: Date.now() });
    setTimeout(() => setFlipFx(null), 1000);
  }, []);

  const onFlip = useCallback((e) => {
    calcSparkle();
    setIsFlipping(true); setCurrentPage(e.data); playPage();
    triggerFlipFx();
    setTimeout(() => setIsFlipping(false), 700);
  }, [playPage, calcSparkle, triggerFlipFx]);

  const flipNext = () => bookRef.current?.pageFlip().flipNext();
  const flipPrev = () => bookRef.current?.pageFlip().flipPrev();

  /* ── RSVP ────────────────────────────────────────────────── */
  const handleRsvpChange = (e) => {
    const { name, value } = e.target;
    setRsvpData(p => ({ ...p, [name]: value }));
    if (rsvpError) setRsvpError('');
  };

  const handleRsvpSubmit = async (e) => {
    e.preventDefault(); setRsvpError(''); setRsvpLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/rsvp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...rsvpData, invitationId: 'camping-na', childName: ev.childName }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json.message || 'Failed to submit RSVP.');
      setRsvpDone(true);
    } catch (err) { setRsvpError(err.message); }
    finally { setRsvpLoading(false); }
  };

  /* ════════════════════════════════ RENDER ═══════════════════ */
  return (
    <div className="lc-root">

      {/* ── Warm daytime sky / atmosphere ── */}
      <div className="lc-sky" aria-hidden="true">
        {/* Golden dust particles */}
        {[...Array(22)].map((_, i) => (
          <div key={i} className="lc-dust"
            style={{
              left: `${(i * 4.3 + 3) % 100}%`,
              top:  `${(i * 11.7 + 4) % 75}%`,
              width:  `${2 + (i % 3)}px`,
              height: `${2 + (i % 3)}px`,
              animationDelay:    `${(i * 0.62) % 6}s`,
              animationDuration: `${5 + (i % 4)}s`,
            }}
          />
        ))}
        {/* Sun */}
        <div className="lc-sun" aria-hidden="true">
          <div className="lc-sun-glow" />
          <div className="lc-sun-core" />
        </div>
        {/* Birds */}
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="lc-bird"
            style={{ top: `${6 + i * 4}%`, animationDelay: `${i * 1.8}s`, animationDuration: `${9 + i * 2}s` }}
          />
        ))}
      </div>

      {/* ── Forest silhouette ── */}
      <div className="lc-forest" aria-hidden="true">
        {[...Array(13)].map((_, i) => (
          <div key={i} className="lc-f-tree"
            style={{
              left:    `${(i * 7.8) % 100}%`,
              height:  `${60 + (i * 23) % 80}px`,
              width:   `${34 + (i * 13) % 28}px`,
              opacity: 0.18 + (i % 4) * 0.06,
            }}
          />
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════
          COVER
      ══════════════════════════════════════════════════════ */}
      {phase === 'cover' && (
        <div className="lc-cover-wrap" onClick={openBook} role="button" aria-label="Open invitation">

          {/* Bunting flags */}
          <div className="lc-bunting" aria-hidden="true">
            <div className="lc-bunting-rope" />
            {['#e0c9a8','#5a8050','#fffdf7','#c8965a','#5a8050','#e0c9a8','#c8965a','#5a8050','#fffdf7'].map((c, i) => (
              <div key={i} className="lc-flag"
                style={{ left: `${i * 11 + 3}%`, background: c, animationDelay: `${i * 0.14}s` }}
              />
            ))}
          </div>

          {/* Card */}
          <div className="lc-cover-card">
            <div className="lc-card-stripe" />

            {/* ONE HAPPY CAMPER */}
            <div className="lc-ohc">
              <span className="lc-ohc-one">ONE</span>
              <h1 className="lc-ohc-happy">HAPPY CAMPER</h1>
            </div>

            {/* Divider */}
            <div className="lc-leaf-div">
              <div className="lc-ld-line" /><span>🌿</span><div className="lc-ld-line" />
            </div>

            {/* ── Baby photo ── */}
            <div className="lc-photo-wrap">
              <div className="lc-photo-outer">
                <div className="lc-photo-ring">
                  <img src={leonPhoto} alt="N.A Leon Villamor Fortuna" className="lc-photo-img" />
                </div>
              </div>
              <div className="lc-photo-badge-bear">🐻</div>
              <div className="lc-photo-badge-leaf">🍃</div>
            </div>

            {/* Name */}
            <div className="lc-name-block">
              <h2 className="lc-cover-name">N.A</h2>
              <p className="lc-cover-sub">First Birthday &amp; Christening</p>
            </div>

            {/* Date pill */}
            <div className="lc-date-pill">
              <span className="lc-dp-m">MAY</span>
              <span className="lc-dp-d">9</span>
              <span className="lc-dp-y">2026</span>
            </div>

            {/* Bear open button */}
            <div className="lc-open-bear-wrap" aria-hidden="true">
              <span className="lc-open-hand">👇</span>
              <div className="lc-open-bear-stage">
                <div className="lc-bear-ring lc-br1" />
                <div className="lc-bear-ring lc-br2" />
                <div className="lc-bear-ring lc-br3" />
                <div className="lc-open-bear">🐻</div>
              </div>
            </div>

            {/* Mini camp scene */}
            <div className="lc-scene" aria-hidden="true">
              {/* Trees */}
              <div className="lc-sc-t lc-sc-tl" /><div className="lc-sc-tk lc-sc-tl" />
              <div className="lc-sc-t lc-sc-tr" /><div className="lc-sc-tk lc-sc-tr" />
              <div className="lc-sc-t lc-sc-tll"/><div className="lc-sc-tk lc-sc-tll"/>
              <div className="lc-sc-t lc-sc-trr"/><div className="lc-sc-tk lc-sc-trr"/>
              {/* Ground */}
              <div className="lc-ground" />
              {/* Tent */}
              <div className="lc-tent">
                <div className="lc-tent-body" />
                <div className="lc-tent-door" />
                <div className="lc-tent-pole"><div className="lc-tent-flag" /></div>
              </div>
              {/* Fire */}
              <div className="lc-fire">
                <div className="lc-fire-stones" />
                <div className="lc-fire-log lc-flog-l" />
                <div className="lc-fire-log lc-flog-r" />
                <div className="lc-flames">
                  <div className="lc-fl lc-fl1" />
                  <div className="lc-fl lc-fl2" />
                  <div className="lc-fl lc-fl3" />
                </div>
              </div>
              <div className="lc-mush-emoji">🍄</div>
            </div>
          </div>

        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          FLIPBOOK
      ══════════════════════════════════════════════════════ */}
      {phase === 'open' && (
        <div className="lc-book-outer">
          <div className="lc-book-header">
            <button className="lc-close-btn" onClick={closeBook}>✕ Close</button>
            <div className="lc-book-title">🏕️ N.A Big Day</div>
            <div className="lc-page-count">{currentPage + 1} / {TOTAL}</div>
          </div>

          <button className="lc-nav-arrow lc-nav-prev" onClick={flipPrev} disabled={currentPage === 0} aria-label="Previous page">‹</button>
          <button className="lc-nav-arrow lc-nav-next" onClick={flipNext} disabled={currentPage >= TOTAL - 1} aria-label="Next page">›</button>

          <div className={`lc-book-stage ${bookReady ? 'ready' : ''}`}>
            {/* Random flip particles */}
            {flipFx && (
              <div
                className={`lc-flip-fx ${flipFx.cls}`}
                key={flipFx.key}
                style={{ left: flipFx.origin.ox, top: flipFx.origin.oy }}
              >
                {flipFx.particles.map(p => (
                  <span
                    key={p.id}
                    className="lc-fx-p"
                    style={{
                      '--fx-x': `${p.x}px`,
                      '--fx-y': `${p.y}px`,
                      '--fx-r': `${p.r}deg`,
                      '--fx-s': p.s,
                      '--fx-d': `${p.d}s`,
                      fontSize: `${p.s + 0.6}rem`,
                    }}
                  >{p.emoji}</span>
                ))}
              </div>
            )}
            <div ref={wrapperRef} className="lc-book-wrap">

              {/* Page-turn sparkle */}
              <div
                className={`lc-sparkle ${isFlipping ? 'on' : ''}`}
                style={{ top: sparklePos.top, left: sparklePos.left }}
                aria-hidden="true"
              >
                {['✦','✧','⋆','✦','✧','⋆','✦','✧'].map((s, i) => (
                  <span key={i} className={`lc-sp lc-sp${i}`}>{s}</span>
                ))}
              </div>

              <HTMLFlipBook
                ref={bookRef}
                width={isMobile ? 340 : 410}
                height={isMobile ? 620 : 580}
                size="stretch"
                minWidth={isMobile ? 300 : 350}
                maxWidth={isMobile ? 390 : 470}
                minHeight={isMobile ? 560 : 500}
                maxHeight={isMobile ? 700 : 660}
                drawShadow
                flippingTime={680}
                usePortrait={isMobile}
                startZIndex={0}
                autoSize={false}
                maxShadowOpacity={0.25}
                showCover
                mobileScrollSupport={false}
                swipeDistance={22}
                clickEventForward
                useMouseEvents
                className="lc-flipbook"
                onFlip={onFlip}
                style={{ margin: '0 auto' }}
              >

                {/* ── P1: Verse + Welcome ── */}
                <Page className="lpg-green">
                  <div className="lpg-in">
                    <div className="lpg-cross">✝</div>
                    <div className="lpg-verse-box">
                      <blockquote className="lpg-verse">{ev.verse}</blockquote>
                      <p className="lpg-verse-ref">— {ev.verseRef}</p>
                    </div>
                    <div className="lpg-welcome">
                      <p className="lpg-wl-pre">Welcome to</p>
                      <p className="lpg-wl-name">N.A</p>
                      <p className="lpg-wl-sub">Little Adventure!</p>
                    </div>
                    <div className="lpg-mini-photo-row">
                      <div className="lpg-mini-ring">
                        <img src={leonPhoto} alt="N.A" className="lpg-mini-photo" />
                      </div>
                    </div>
                    <div className="lpg-page-trees" aria-hidden="true">
                      <div className="lpg-pt lpg-pt1" /><div className="lpg-pt lpg-pt2" /><div className="lpg-pt lpg-pt3" />
                    </div>
                  </div>
                </Page>

                {/* ── P2: Invitation ── */}
                <Page className="lpg-parch">
                  <div className="lpg-in">
                    <div className="lpg-leaf-hd">🍃 🌿 🍃</div>
                    <p className="lpg-with">TOGETHER WITH HER FAMILY</p>
                    <p className="lpg-with">AND THE BLESSINGS OF GOD ABOVE</p>
                    <div className="lpg-mother-block">
                      <p className="lpg-mb-lbl">Father</p>
                      <p className="lpg-mb-name">{ev.father}</p>
                      <div className="lpg-parent-amp">&amp;</div>
                      <p className="lpg-mb-lbl">Mother</p>
                      <p className="lpg-mb-name">{ev.mother}</p>
                    </div>
                    <p className="lpg-inv-line">JOYFULLY INVITES YOU TO CELEBRATE</p>
                    <div className="lpg-child-hero">
                      <h2 className="lpg-child-name">N.A</h2>
                      <p className="lpg-child-full">{ev.childFull}</p>
                    </div>
                    <div className="lpg-event-band">
                      <p className="lpg-eb-title">FIRST BIRTHDAY & CHRISTENING</p>
                      <div className="lpg-eb-date-row">
                        <span className="lpg-eb-m">MAY</span>
                        <span className="lpg-eb-d">9</span>
                        <span className="lpg-eb-y">2026</span>
                      </div>
                      <p className="lpg-eb-day">{ev.eventDay}</p>
                    </div>
                    <div className="lpg-foot">🌲&nbsp;🏕️&nbsp;🌲</div>
                  </div>
                </Page>

                {/* ── P3: Christening ── */}
                <Page className="lpg-parch">
                  <div className="lpg-in">
                    <div className="lpg-sec-hd lpg-hd-green">
                      <span>✝️</span><h3>Christening</h3>
                    </div>

                    <div className="lpg-ev-card">
                      <div className="lpg-evc-time">
                        <span className="lpg-evc-tl">TIME</span>
                        <span className="lpg-evc-tv">{ev.timeChurch}</span>
                      </div>
                      <p className="lpg-evc-abbr">{ev.churchAbbr}</p>
                      <p className="lpg-evc-name">{ev.church}</p>
                      <p className="lpg-evc-addr">{ev.churchAddr}</p>
                      <a
                        className="lpg-map-btn"
                        href="https://maps.google.com/?q=Our+Lady+of+the+Annunciation+Parish+Mindanao+Ave+Quezon+City"
                        target="_blank" rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                      >📍 Open in Maps</a>
                    </div>

                    <div className="lpg-divider">✝️ Holy Baptism ✝️</div>

                    <div className="lpg-note-box">
                      <p>On this sacred day, little <strong>N.A</strong></p>
                      <p>receives the gift of faith and love.</p>
                      <p>We are overjoyed to share this</p>
                      <p>blessing with our loved ones.</p>
                    </div>

                    <div className="lpg-dove">🕊️</div>
                    <div className="lpg-foot">✝️&nbsp;🕊️&nbsp;✝️</div>
                  </div>
                </Page>

                {/* ── P4: Birthday Reception + Program ── */}
                <Page className="lpg-parch">
                  <div className="lpg-in">
                    <div className="lpg-sec-hd lpg-hd-orange">
                      <span>🎂</span><h3>Birthday Reception</h3>
                    </div>

                    <div className="lpg-ev-card">
                      <div className="lpg-evc-time">
                        <span className="lpg-evc-tl">TIME</span>
                        <span className="lpg-evc-tv">{ev.timeRec} – {ev.timeEnd}</span>
                      </div>
                      <p className="lpg-evc-name">{ev.venue}</p>
                      <p className="lpg-evc-addr">{ev.venueAddr}</p>
                      <a
                        className="lpg-map-btn"
                        href="https://maps.google.com/?q=Peri-Peri+Mindanao+Ave+Quezon+City"
                        target="_blank" rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                      >📍 Open in Maps</a>
                    </div>

                    <div className="lpg-divider">🏕️ Program 🏕️</div>

                    <div className="lpg-program">
                      {ev.program.map((item, i) => (
                        <div key={i} className="lpg-prog-row">
                          <span className="lpg-pr-t">{item.time}</span>
                          <div className="lpg-pr-dot" />
                          <span className="lpg-pr-e">{item.event}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Page>

                {/* ── P5: Baby Photo Spotlight ── */}
                <Page className="lpg-photo-page">
                  <div className="lpg-in lpg-photo-in">
                    <p className="lpg-photo-lbl">OUR LITTLE CAMPER</p>

                    <div className="lpg-hero-wrap">
                      <div className="lpg-hero-outer">
                        <div className="lpg-hero-ring">
                          <img src={leonPhoto} alt="N.A Leon Villamor Fortuna" className="lpg-hero-photo" />
                        </div>
                      </div>
                      <div className="lpg-hero-badge-l">🐻</div>
                      <div className="lpg-hero-badge-r">⛺</div>
                    </div>

                    <div className="lpg-photo-name-block">
                      <h2 className="lpg-pnb-name">N.A</h2>
                      <p className="lpg-pnb-full">{ev.childFull}</p>
                    </div>

                    <p className="lpg-photo-tagline">"Hike on over to help us celebrate!"</p>
                    <div className="lpg-photo-dec">🌲&nbsp;&nbsp;🍄&nbsp;&nbsp;🌿&nbsp;&nbsp;🍄&nbsp;&nbsp;🌲</div>
                  </div>
                </Page>

                {/* ── P6: Countdown + Save the Date ── */}
                <Page className="lpg-parch">
                  <div className="lpg-in">
                    <div className="lpg-sec-hd lpg-hd-forest">
                      <span>⏳</span><h3>Counting Down</h3>
                    </div>

                    <div className="lpg-countdown">
                      {[{ v: countdown.d, l: 'DAYS' }, { v: countdown.h, l: 'HRS' }, { v: countdown.m, l: 'MIN' }, { v: countdown.s, l: 'SEC' }].map((c, i) => (
                        <React.Fragment key={i}>
                          <div className="lpg-cd-unit">
                            <span className="lpg-cd-n">{String(c.v).padStart(2, '0')}</span>
                            <span className="lpg-cd-l">{c.l}</span>
                          </div>
                          {i < 3 && <span className="lpg-cd-colon">:</span>}
                        </React.Fragment>
                      ))}
                    </div>

                    <div className="lpg-divider">🏕️ Save the Date 🏕️</div>

                    <div className="lpg-save-card">
                      <p className="lpg-sc-title">N.A BIG DAY</p>
                      <p className="lpg-sc-date">{ev.eventDate}</p>
                      <p className="lpg-sc-day">{ev.eventDay}</p>
                      <div className="lpg-sc-events">
                        <div className="lpg-sc-ev">
                          <span className="lpg-sc-ei">✝️</span>
                          <div>
                            <p className="lpg-sc-et">{ev.timeChurch} • Christening</p>
                            <p className="lpg-sc-ep">{ev.church}</p>
                          </div>
                        </div>
                        <div className="lpg-sc-sep">🌿</div>
                        <div className="lpg-sc-ev">
                          <span className="lpg-sc-ei">🎂</span>
                          <div>
                            <p className="lpg-sc-et">{ev.timeRec}–{ev.timeEnd} • Reception</p>
                            <p className="lpg-sc-ep">{ev.venue}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Page>

                {/* ── P7: Snap + Gift + RSVP ── */}
                <Page className="lpg-parch">
                  <div className="lpg-in">
                    <div className="lpg-sec-hd lpg-hd-brown">
                      <span>📸</span><h3>Snap &amp; Share</h3>
                    </div>
                    <p className="lpg-snap-txt">CAPTURE YOUR MEMORIES!</p>
                    <div className="lpg-hashtag-wrap">
                      <p className="lpg-hashtag">{ev.hashtag}</p>
                    </div>

                    {/* ── Photo Gallery ── */}
                    <div className="lpg-gallery">
                      {galleryPhotos.map((src, i) => (
                        <button
                          key={i}
                          className={`lpg-gal-thumb lpg-gal-t${i}`}
                          onClick={e => { e.stopPropagation(); setGalleryIdx(i); }}
                          aria-label={`View photo ${i + 1}`}
                        >
                          <img src={src} alt={`N.A memory ${i + 1}`} className="lpg-gal-img" />
                          <div className="lpg-gal-overlay"><span>🔍</span></div>
                        </button>
                      ))}
                    </div>

                    <div className="lpg-divider">🎁 Gift Note 🎁</div>
                    <div className="lpg-gift-box">
                      <p>Your presence is the greatest gift! 🧸</p>
                      <p>Should you wish to bless little <strong>N.A</strong>,</p>
                      <p>any token of love is warmly appreciated.</p>
                    </div>

                    <div className="lpg-divider">💌 RSVP 💌</div>
                    <p className="lpg-rsvp-note">Kindly respond before {ev.rsvpBy}</p>
                    {rsvpOpen ? (
                      <button
                        className="lpg-rsvp-btn"
                        onClick={e => { e.stopPropagation(); setShowRsvp(true); }}
                      >🏕️ RSVP Now</button>
                    ) : (
                      <p className="lpg-rsvp-closed">RSVPs are now closed</p>
                    )}
                  </div>
                </Page>

                {/* ── P8: Thank You ── */}
                <Page className="lpg-dark">
                  <div className="lpg-in lpg-ty">
                    <div className="lpg-ty-fire" aria-hidden="true">
                      <div className="lpg-ty-fl ty1" /><div className="lpg-ty-fl ty2" /><div className="lpg-ty-fl ty3" />
                      <div className="lpg-ty-ll" /><div className="lpg-ty-lr" />
                    </div>
                    <h2 className="lpg-ty-title">Thank You</h2>
                    <p className="lpg-ty-msg">for celebrating with us!</p>
                    <div className="lpg-ty-mini-photo">
                      <img src={leonPhoto} alt="N.A" className="lpg-ty-photo" />
                    </div>
                    <div className="lpg-ty-name">N.A</div>
                    <p className="lpg-ty-date">{ev.eventDate}</p>
                    <div className="lpg-ty-icons">🐻 🏕️ 🎂 ✝️ 🎉</div>
                    <p className="lpg-ty-from">With love, {ev.father} &amp; {ev.mother}</p>
                  </div>
                </Page>

              </HTMLFlipBook>
            </div>
          </div>

          {isMobile && <p className="lc-swipe-hint">← Swipe or tap ‹ › to turn →</p>}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          PHOTO LIGHTBOX
      ══════════════════════════════════════════════════════ */}
      {galleryIdx !== null && (
        <div className="lpg-lightbox" onClick={() => setGalleryIdx(null)}>
          <div className="lpg-lb-inner" onClick={e => e.stopPropagation()}>
            <button className="lpg-lb-close" onClick={() => setGalleryIdx(null)} aria-label="Close">✕</button>

            {galleryIdx > 0 && (
              <button className="lpg-lb-arrow lpg-lb-prev"
                onClick={() => setGalleryIdx(i => i - 1)}
                aria-label="Previous photo">‹</button>
            )}

            <img
              key={galleryIdx}
              src={galleryPhotos[galleryIdx]}
              alt={`N.A memory ${galleryIdx + 1}`}
              className="lpg-lb-photo"
            />

            {galleryIdx < galleryPhotos.length - 1 && (
              <button className="lpg-lb-arrow lpg-lb-next"
                onClick={() => setGalleryIdx(i => i + 1)}
                aria-label="Next photo">›</button>
            )}

            <div className="lpg-lb-dots">
              {galleryPhotos.map((_, i) => (
                <button
                  key={i}
                  className={`lpg-lb-dot${galleryIdx === i ? ' active' : ''}`}
                  onClick={() => setGalleryIdx(i)}
                  aria-label={`Photo ${i + 1}`}
                />
              ))}
            </div>

            <p className="lpg-lb-counter">{galleryIdx + 1} / {galleryPhotos.length}</p>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          RSVP MODAL
      ══════════════════════════════════════════════════════ */}
      {showRsvp && (
        <div className="lc-rsvp-overlay" onClick={() => setShowRsvp(false)}>
          <div className="lc-rsvp-modal" onClick={e => e.stopPropagation()}>
            <button className="lc-rsvp-x" onClick={() => setShowRsvp(false)}>✕</button>

            {!rsvpDone ? (
              <>
                <div className="lc-rsvp-head">
                  <div className="lc-rsvp-photo-wrap">
                    <img src={leonPhoto} alt="N.A" className="lc-rsvp-photo" />
                  </div>
                  <h2>RSVP</h2>
                  <p>Join us at Camp N.A! 🏕️</p>
                </div>
                <form onSubmit={handleRsvpSubmit} noValidate>
                  <div className="lc-field">
                    <label htmlFor="lc-name">Your Name</label>
                    <input id="lc-name" type="text" name="name" value={rsvpData.name} onChange={handleRsvpChange} placeholder="Enter your full name" required />
                  </div>
                  <div className="lc-field">
                    <label>Will you attend?</label>
                    <div className="lc-choices">
                      <label className={`lc-choice ${rsvpData.attending === 'yes' ? 'sel' : ''}`}>
                        <input type="radio" name="attending" value="yes" checked={rsvpData.attending === 'yes'} onChange={handleRsvpChange} required />
                        🎉 Joyfully Accept
                      </label>
                      <label className={`lc-choice ${rsvpData.attending === 'no' ? 'sel' : ''}`}>
                        <input type="radio" name="attending" value="no" checked={rsvpData.attending === 'no'} onChange={handleRsvpChange} />
                        😢 Regretfully Decline
                      </label>
                    </div>
                  </div>
                  {rsvpData.attending === 'yes' && (
                    <div className="lc-field">
                      <label htmlFor="lc-guests">Number of Guests</label>
                      <select id="lc-guests" name="guests" value={rsvpData.guests} onChange={handleRsvpChange}>
                        {['1','2','3','4','5+'].map(n => <option key={n} value={n}>{n} Guest{n !== '1' ? 's' : ''}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="lc-field">
                    <label htmlFor="lc-msg">Message (Optional)</label>
                    <textarea id="lc-msg" name="message" value={rsvpData.message} onChange={handleRsvpChange} placeholder="Write your wishes for little N.A…" rows="3" />
                  </div>
                  {rsvpError && <p className="lc-rsvp-err">{rsvpError}</p>}
                  <button type="submit" className="lc-rsvp-submit" disabled={rsvpLoading}>
                    {rsvpLoading ? 'Sending…' : '🏕️ Send RSVP'}
                  </button>
                </form>
              </>
            ) : (
              <div className="lc-rsvp-success">
                <div className="lc-rs-photo-wrap">
                  <img src={leonPhoto} alt="N.A" className="lc-rs-photo" />
                </div>
                <h2>Thank You!</h2>
                <p>RSVP received,</p>
                <p className="lc-rs-name">{rsvpData.name}!</p>
                {rsvpData.attending === 'yes'
                  ? <p>We can't wait to see you at Camp N.A! 🏕️</p>
                  : <p>We'll miss you! Thank you for letting us know. 💕</p>
                }
                <button className="lc-rs-close" onClick={() => setShowRsvp(false)}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Watermark */}
      <div className="lc-watermark" aria-hidden="true">
        <p>Created with ❤️ by</p>
        <img src={logicaLogo} alt="Logica Technology" />
      </div>

      {/* Music toggle */}
      <button
        className={`lc-music-btn${bgPlaying ? ' playing' : ''}`}
        onClick={toggleBg}
        aria-label={bgPlaying ? 'Pause background music' : 'Play background music'}
      >
        <span className="lc-music-icon">{bgPlaying ? '🎵' : '🔇'}</span>
        {bgPlaying && (
          <span className="lc-music-bars" aria-hidden="true">
            <span /><span /><span /><span />
          </span>
        )}
      </button>

    </div>
  );
}
